/**
 * Helper functions for organization data processing
 */

// Get plan metadata reducer based on type
export const getPlanMetadataTypeReducer = (type) => {
    switch (type) {
        case 'number':
            return (acc, curr) => Math.max(parseInt(acc) || 0, parseInt(curr) || 0)
        default:
            return (acc, curr) => acc || curr === 'true'
    }
}

// Plan metadata types mapping
export const PLAN_METADATA_TYPES = {
    '3d': 'boolean',
    customer_pricing: 'boolean',
    customer_supplied: 'boolean',
    fixed_price_parts: 'boolean',
    is_enterprise_plan: 'boolean',
    material_consumption: 'boolean',
    materials_revamp: 'boolean',
    max_users: 'number',
    miscellaneous_items: 'boolean',
    part_library: 'boolean',
    pdf: 'boolean',
    plan: 'free',
    order_history: 'boolean',
    sells_drawing_credits: 'boolean',
    tube: 'boolean',
    webstore: 'boolean',
    showViewer3D: 'boolean',
    showViewerTube: 'boolean',
    shipping: 'boolean',
}

/**
 * Extract metadata from subscriptions
 * @param {Array} subscriptions - The organization's subscriptions
 * @returns {Object} Metadata keyed by priceId
 */
export const extractMetadataFromSubscriptions = (subscriptions) => {
    if (!subscriptions || !subscriptions.length) {
        return {}
    }

    // Extract price data from subscription items
    const prices = subscriptions
        .map((s) => s.subscriptionItems)
        .reduce((array, current) => {
            current.forEach((si) => array.push(si.price))
            return array
        }, [])

    // Create metadata object with priceId keys
    return prices.reduce((map, curr) => {
        if (curr && curr.metadata) {
            const { $type, ...metadataDto } = curr.metadata
            map[curr.priceId] = metadataDto
        }
        return map
    }, {})
}

/**
 * Resolve metadata from multiple plans
 * @param {Object} metadata - The metadata keyed by priceId
 * @returns {Object} Resolved metadata
 */
export const resolveMetadata = (metadata) => {
    if (!metadata || Object.keys(metadata).length === 0) {
        return {}
    }

    return Object.values(metadata).reduce((acc, currPlanMetadata) => {
        return Object.keys(currPlanMetadata).reduce((innerAcc, metadataKey) => {
            innerAcc[metadataKey] = getPlanMetadataTypeReducer(PLAN_METADATA_TYPES[metadataKey])(
                innerAcc[metadataKey],
                currPlanMetadata[metadataKey]
            )
            return innerAcc
        }, acc)
    }, {})
}

/**
 * Determine paid features based on subscription data
 * @param {Object} resolvedMetadata - The resolved metadata
 * @param {boolean} isOnTrialValue - Whether the organization is on trial
 * @param {boolean} hasActiveSubscription - Whether the organization has an active subscription
 * @returns {Object} Paid features
 */
export const determinePaidFeatures = (resolvedMetadata, isOnTrialValue, hasActiveSubscription) => {
    return {
        isFreePlan: resolvedMetadata?.plan?.toString().toLowerCase() === 'free',
        hasMaterialConsumptionModeOptions:
            isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.material_consumption),
        hasCustomerPricing: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.customer_pricing),
        hasAdvancedMaterials: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.materials_revamp),
        hasCustomerSuppliedMaterial: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.customer_supplied),
        hasCreditConsumption: true,
        hasUnfoldTube: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.tube),
        hasPdfUpload: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.pdf),
        hasPartLibrary: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.part_library),
        hasWebStore: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.webstore),
        has3d: isOnTrialValue || (hasActiveSubscription && resolvedMetadata ? resolvedMetadata['3d'] : false),
        hasMiscellaneousItems: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.miscellaneous_items),
        hasFixedPriceParts: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.fixed_price_parts),
        hasShowViewer3D: hasActiveSubscription && resolvedMetadata?.ShowViewer3D,
        hasShowViewerTube: hasActiveSubscription && resolvedMetadata?.ShowViewerTube,
        hasShipping: isOnTrialValue || (hasActiveSubscription && resolvedMetadata?.shipping),
    }
}
